import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  FileText, 
  RefreshCw, 
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  BarChart3,
  TrendingUp,
  Target,
  CheckCircle,
  ChevronDown,
  Edit,
  Camera,
  AlertTriangle
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Logo from './Logo';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const { isDark, isMenuCollapsed, toggleTheme, toggleMenu } = useTheme();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown')) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/clientes', icon: Users, label: 'Clientes' },
    { path: '/clientes-em-aberto', icon: AlertTriangle, label: 'Clientes em Aberto' },
    { path: '/contas-receber', icon: FileText, label: 'Contas a Receber' },
    { path: '/controle-cobranca', icon: Settings, label: 'Controle de Cobrança' },
    { path: '/finalizacao-contas', icon: CheckCircle, label: 'Finalização de Contas' },
    { path: '/sincronizacao', icon: RefreshCw, label: 'Sincronização' },
    { path: '/analises', icon: BarChart3, label: 'Análises' },
  ];

  // Adicionar gestão apenas para administradores
  if (user?.tipo_usuario === 'master') {
    menuItems.push(
      { path: '/efetividade', icon: TrendingUp, label: 'Efetividade' },
      { path: '/gestao', icon: BarChart3, label: 'Gestão de Cobrança' },
      { path: '/gestao-usuarios', icon: Users, label: 'Gestão de Usuários' }
    );
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ${
        isMenuCollapsed ? 'w-16' : 'w-64'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={toggleMenu}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isMenuCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
          
          {!isMenuCollapsed && (
            <div className="text-center">
              <h1 className="text-sm font-medium text-gray-900 dark:text-white">
                Sistema de Cobrança
              </h1>
            </div>
          )}
          
          <div className="w-10"></div> {/* Espaçador para manter o título centralizado */}
        </div>

        {/* Navigation */}
        <nav className="mt-2 sm:mt-4 px-1 sm:px-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-2 sm:px-3 py-2 mb-1 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={isMenuCollapsed ? item.label : ''}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!isMenuCollapsed && (
                  <span className="ml-2 sm:ml-3 text-sm font-medium truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Theme Toggle */}
        <div className="absolute bottom-12 left-0 right-0 px-1 sm:px-2">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-center px-2 sm:px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title={isMenuCollapsed ? (isDark ? 'Modo Claro' : 'Modo Escuro') : ''}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
            {!isMenuCollapsed && (
              <span className="ml-2 sm:ml-3 text-sm font-medium truncate">
                {isDark ? 'Modo Claro' : 'Modo Escuro'}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        isMenuCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        {/* Header com Perfil */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-4">
              <Logo size="md" showText={true} />
            </div>
            
            {/* Dropdown de Perfil */}
            {user && (
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user.first_name?.[0] || user.username[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.first_name || user.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user.tipo_usuario === 'master' ? 'Administrador' : 'Funcionário'}
                    </div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${
                    showProfileDropdown ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* Dropdown Menu */}
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          // TODO: Implementar edição de perfil
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-3" />
                        Editar Perfil
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          // TODO: Implementar upload de foto
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Camera className="h-4 w-4 mr-3" />
                        Alterar Foto
                      </button>
                      
                      <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                        <button
                          onClick={() => {
                            setShowProfileDropdown(false);
                            onLogout();
                          }}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Sair
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
        
        <main className="p-3 sm:p-4 lg:p-6 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 